import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, Paper } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { cellToLatLng } from 'h3-js';

import { getOneSpecies } from '../../actions/species';
import { fetchSpeciesEnrichment } from '../../lib/speciesEnrichment';
import MapComponent from './MapComponent';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatCount(value) {
  if (!Number.isFinite(Number(value))) return '0';
  return Number(value).toLocaleString();
}

function formatCoordinate(value) {
  if (!Number.isFinite(Number(value))) return 'Unknown';
  return Number(value).toFixed(3);
}

function buildSpeciesAnalysis(oneSpecies) {
  const hotspots = Array.isArray(oneSpecies?.hotspots) ? oneSpecies.hotspots : [];
  const monthDist = new Array(12).fill(0);
  const mappedHotspots = [];

  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;

  hotspots.forEach((hotspot) => {
    const h3Cell = hotspot?.h3_location || hotspot?.h3;
    if (!h3Cell) return;

    let coordinates;
    try {
      coordinates = cellToLatLng(h3Cell);
    } catch (_error) {
      return;
    }

    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      return;
    }

    const [lat, lng] = coordinates;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);

    const rawMonthDist = Array.isArray(hotspot.month_dist) ? hotspot.month_dist : [];
    const hotspotMonthDist = new Array(12).fill(0).map((_, index) => Number(rawMonthDist[index] || 0));
    hotspotMonthDist.forEach((value, index) => {
      monthDist[index] += value;
    });

    mappedHotspots.push({
      id: hotspot.item_type_id || hotspot._id || hotspot.h3_location || hotspot.h3,
      h3: h3Cell,
      coordinates,
      count: Number(
        hotspot.observations_count ?? hotspot.count ?? hotspot.hotspot_count ?? hotspot.observation_count ?? 0
      ),
      monthDist: hotspotMonthDist,
    });
  });

  const peakValue = Math.max(...monthDist, 0);
  const peakMonth =
    peakValue > 0 ? MONTH_LABELS[monthDist.findIndex((value) => value === peakValue)] : 'No clear peak yet';
  const strongestMonths = MONTH_LABELS.map((label, index) => ({ label, count: monthDist[index] }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const topHotspots = [...mappedHotspots].sort((a, b) => b.count - a.count).slice(0, 6);

  const mapData = {
    locations: mappedHotspots.map((item) => ({
      coordinates: item.coordinates,
      count: item.count,
      month_dist: item.monthDist,
    })),
    minLat,
    maxLat,
    minLong: minLng,
    maxLong: maxLng,
  };

  const hasBounds = mappedHotspots.length > 0;
  const mapCenter = hasBounds
    ? {
        lat: (maxLat + minLat) / 2,
        lng: (maxLng + minLng) / 2,
      }
    : { lat: 36.95, lng: -122.1 };

  return {
    hotspots,
    monthDist,
    peakMonth,
    peakValue,
    strongestMonths,
    topHotspots,
    mapData,
    mapCenter,
    mappedHotspotCount: mappedHotspots.length,
    hotspotObservationCount: mappedHotspots.reduce((sum, hotspot) => sum + hotspot.count, 0),
  };
}

export const SpeciesDetails = () => {
  const { oneSpecies, isLoading } = useSelector((state) => state.species);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [enrichment, setEnrichment] = useState(null);
  const [isEnrichmentLoading, setIsEnrichmentLoading] = useState(false);

  useEffect(() => {
    dispatch(getOneSpecies(id));
  }, [id, dispatch]);

  const analysis = useMemo(() => buildSpeciesAnalysis(oneSpecies), [oneSpecies]);

  const species = oneSpecies?.species || {};
  const hasSpeciesRecord = Boolean(oneSpecies);
  const displayName = species.name || species.common_name || species.species || 'Species detail';
  const scientificName =
    species.species || species.scientific_name || species.canonical_name || 'Scientific name pending';

  useEffect(() => {
    if (!hasSpeciesRecord) {
      setEnrichment(null);
      return;
    }

    let active = true;
    setIsEnrichmentLoading(true);

    fetchSpeciesEnrichment({
      scientificName,
      commonName: displayName !== scientificName ? displayName : '',
    })
      .then((payload) => {
        if (!active) return;
        setEnrichment(payload);
      })
      .finally(() => {
        if (!active) return;
        setIsEnrichmentLoading(false);
      });

    return () => {
      active = false;
    };
  }, [displayName, scientificName, hasSpeciesRecord]);

  if (isLoading) {
    return (
      <div className="atlas-route-shell atlas-detail-page">
        <Paper elevation={6} className="atlas-detail-card atlas-loading-shell">
          <CircularProgress size="3rem" />
        </Paper>
      </div>
    );
  }

  if (!oneSpecies) {
    return (
      <div className="atlas-route-shell atlas-detail-page">
        <div className="atlas-empty-state">This species record is unavailable right now.</div>
      </div>
    );
  }

  const taxonKey = species.taxonKey || species.taxon_key || species.item_type_id || id;
  const totalObservations = species.count ?? analysis.hotspotObservationCount;
  const totalHotspots = species.hotspot_count ?? analysis.hotspots.length;
  const totalEvents = species.event_count ?? species.events_count ?? 0;
  const maxMonthCount = Math.max(...analysis.monthDist, 0);
  const hasGoogleMap = Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY && analysis.mapData.locations.length);
  const supportLinks = Array.isArray(enrichment?.supportLinks) ? enrichment.supportLinks : [];
  const heroImage = enrichment?.image;
  const rangeMap = enrichment?.rangeMap;
  const statusTone = enrichment?.iucnStatusTone || 'pending';
  const statusLabel = enrichment?.iucnStatus || 'Open-data lookup pending';
  const iucnReference = enrichment?.iucnReference || '';
  const strongestMonthText = analysis.strongestMonths.length
    ? analysis.strongestMonths.map((item) => `${item.label} (${formatCount(item.count)})`).join(', ')
    : 'No monthly signal yet';

  return (
    <div className="atlas-route-shell atlas-detail-page">
      <section className="atlas-page-header atlas-detail-hero atlas-detail-hero--rich">
        <div className="atlas-detail-hero-copy">
          <span className="atlas-eyebrow">Species intelligence</span>
          <h1>{displayName}</h1>
          <p>
            Start with the open profile, then move into Tellus signal. This page is designed to help
            you quickly answer four questions: what the species is, what its conservation context
            looks like, when it tends to surface, and where to go next.
          </p>
          <div className="atlas-pill-row">
            <span className="atlas-pill">{scientificName}</span>
            <span className={`atlas-pill atlas-pill--status atlas-pill--${statusTone}`}>IUCN {statusLabel}</span>
            <span className="atlas-pill">Peak month {analysis.peakMonth}</span>
            <span className="atlas-pill">Taxon ID {taxonKey}</span>
          </div>
          <div className="atlas-detail-list">
            <span>
              <strong>Open profile</strong>
              {isEnrichmentLoading
                ? 'Loading open species context…'
                : enrichment?.summary || 'Open species context is still being assembled.'}
            </span>
          </div>
        </div>

        <div className="atlas-species-visual-card">
          {heroImage?.src ? (
            <div className="atlas-species-visual-frame">
              <img src={heroImage.src} alt={displayName} className="atlas-species-hero-image" />
            </div>
          ) : (
            <div className="atlas-empty-state atlas-empty-state--compact">No open image linked yet.</div>
          )}
          <div className="atlas-detail-stack">
            <span>
              <strong>Image source</strong>
              {heroImage?.pageUrl ? (
                <a href={heroImage.pageUrl} target="_blank" rel="noreferrer">
                  {heroImage.attributionLabel || 'Open media source'}
                </a>
              ) : (
                'Open media source pending'
              )}
            </span>
            <span>
              <strong>Best for</strong>
              Learn the species, gauge the conservation context, then compare Tellus hotspots and seasonality below.
            </span>
          </div>
        </div>
      </section>

      <div className="atlas-detail-grid">
        <section className="atlas-detail-stack">
          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">At a glance</span>
            <div className="atlas-metric-row atlas-metric-row--quad">
              <div>
                <strong>{formatCount(totalObservations)}</strong>
                <span>observations</span>
              </div>
              <div>
                <strong>{formatCount(totalHotspots)}</strong>
                <span>hotspots</span>
              </div>
              <div>
                <strong>{formatCount(totalEvents)}</strong>
                <span>events</span>
              </div>
              <div>
                <strong>{formatCount(analysis.mappedHotspotCount)}</strong>
                <span>mapped cells</span>
              </div>
            </div>
          </Paper>

          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">How to use this page</span>
            <div className="atlas-detail-list">
              <span>
                <strong>1. Understand the species</strong>
                Read the open profile and status before using the activity signal as a proxy for abundance.
              </span>
              <span>
                <strong>2. Find the right season</strong>
                Start with {analysis.peakMonth} and then compare {strongestMonthText}.
              </span>
              <span>
                <strong>3. Choose where to go next</strong>
                Use the top hotspots and map preview to focus the next field check or trip plan.
              </span>
            </div>
          </Paper>

          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Seasonality</span>
            <div className="atlas-detail-stack">
              <div className="atlas-detail-list">
                <span>
                  <strong>Strongest months</strong>
                  {strongestMonthText}
                </span>
              </div>
              <div className="atlas-seasonality-list">
                {MONTH_LABELS.map((label, index) => {
                  const count = analysis.monthDist[index];
                  const width = maxMonthCount > 0 ? Math.max((count / maxMonthCount) * 100, count > 0 ? 8 : 0) : 0;
                  return (
                    <div key={label} className="atlas-season-row">
                      <div className="atlas-season-row-head">
                        <span>{label}</span>
                        <strong>{formatCount(count)}</strong>
                      </div>
                      <div className="atlas-season-bar">
                        <span style={{ width: `${width}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Paper>
        </section>

        <section className="atlas-detail-stack">
          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Status and range</span>
            <div className="atlas-split-card">
              <div className="atlas-detail-stack">
                <span>
                  <strong>IUCN status</strong>
                  {statusLabel}
                </span>
                <span>
                  <strong>IUCN reference</strong>
                  {iucnReference || 'Official taxon reference pending'}
                </span>
                <span>
                  <strong>Range map</strong>
                  {rangeMap?.pageUrl ? (
                    <a href={rangeMap.pageUrl} target="_blank" rel="noreferrer">
                      Open range and spatial references
                    </a>
                  ) : (
                    'Range reference pending'
                  )}
                </span>
              </div>

              {rangeMap?.src ? (
                <div className="atlas-range-map-frame">
                  <img src={rangeMap.src} alt={`${displayName} range map`} className="atlas-range-map-image" />
                </div>
              ) : (
                <div className="atlas-detail-list">
                  <span>
                    <strong>Range note</strong>
                    When a linked range image is unavailable, the IUCN spatial-data link still gives you an official next step.
                  </span>
                </div>
              )}
            </div>
          </Paper>

          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Open data and action</span>
            <div className="atlas-link-card-grid">
              {supportLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="atlas-resource-card"
                >
                  <strong>{link.label}</strong>
                  <span>{link.description}</span>
                </a>
              ))}
            </div>
          </Paper>

          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Top hotspots</span>
            {analysis.topHotspots.length ? (
              <div className="atlas-upcoming-grid atlas-upcoming-grid--single">
                {analysis.topHotspots.map((hotspot, index) => (
                  <article key={hotspot.id} className="atlas-upcoming-card">
                    <h4>Hotspot {index + 1}</h4>
                    <div className="atlas-detail-stack">
                      <span>
                        <strong>Observations</strong>
                        {formatCount(hotspot.count)}
                      </span>
                      <span>
                        <strong>Latitude</strong>
                        {formatCoordinate(hotspot.coordinates[0])}
                      </span>
                      <span>
                        <strong>Longitude</strong>
                        {formatCoordinate(hotspot.coordinates[1])}
                      </span>
                      <span>
                        <strong>Cell reference</strong>
                        {hotspot.h3}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="atlas-empty-state atlas-empty-state--compact">
                No hotspot geometry is available for this species yet.
              </div>
            )}
          </Paper>

          <Paper elevation={6} className="atlas-detail-card">
            <span className="atlas-card-kicker">Spatial preview</span>
            {hasGoogleMap ? (
              <MapComponent
                locations={analysis.mapData.locations}
                month_dist={analysis.monthDist}
                center={analysis.mapCenter}
              />
            ) : (
              <div className="atlas-detail-stack">
                <p className="atlas-card-copy">
                  The structured hotspot and seasonality summary is ready, but the Google Maps layer
                  only appears when `REACT_APP_GOOGLE_MAPS_API_KEY` is configured.
                </p>
                <div className="atlas-detail-list">
                  <span>
                    <strong>Map status</strong>
                    {analysis.mapData.locations.length
                      ? 'Hotspot geometry is available and ready for map rendering.'
                      : 'No hotspot geometry is available yet for this species.'}
                  </span>
                </div>
              </div>
            )}
          </Paper>
        </section>
      </div>
    </div>
  );
};
